import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, yen, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Truck, PackageCheck, Phone, MapPin, Trash2, MessageCircle } from "lucide-react";
import { OrderContactButtons } from "../components/OrderContactButtons";

const STATUSES = ["new", "processing", "done", "cancelled"];
const colors = {
  new: "bg-primary/10 text-primary border-primary/30",
  processing: "bg-blue-50 text-blue-700 border-blue-200",
  done: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-gray-100 text-gray-500 border-gray-200",
};

export default function PesananOnline() {
  const { t } = useI18n();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(
    () => api.get("/online-orders").then(({ data }) => setOrders(data)),
    []
  );
  useEffect(() => {
    load();
    const timer = setInterval(load, 20000);
    return () => clearInterval(timer);
  }, [load]);

  const setStatus = async (o, status) => {
    try {
      await api.patch(`/online-orders/${o.id}`, { status });
      await load();
      toast.success(t("status_updated"));
    } catch (e) {
      toast.error(errMsg(e, "Gagal memperbarui status"));
    }
  };

  const list = orders.filter((o) => filter === "all" || o.status === filter);

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold">{t("online_title")}</h1>
        <p className="text-sm text-muted-foreground">{t("online_sub")}</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {["all", ...STATUSES].map((s) => (
          <button
            key={s}
            data-testid={`online-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
              filter === s ? "bg-gray-900 text-white" : "bg-white border border-gray-200"
            }`}
          >
            {s === "all" ? t("all") : t(`st_${s}`)}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="online-orders-list">
        {list.length === 0 && (
          <p className="text-sm text-muted-foreground">{t("no_online_orders")}</p>
        )}
        {list.map((o) => (
          <div
            key={o.id}
            data-testid={`online-order-${o.id}`}
            className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-medium">{o.order_no}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(o.created_at).toLocaleString("id-ID")}
                </p>
              </div>
              <span className={`text-xs px-2.5 py-1 rounded-full border ${colors[o.status]}`}>
                {t(`st_${o.status}`)}
              </span>
            </div>

            <div className="flex flex-wrap gap-2 text-xs">
              <Badge variant="secondary">
                {o.channel === "resto" ? t("channel_resto") : t("channel_grocery")}
              </Badge>
              <Badge variant="outline" className="flex items-center gap-1">
                {o.fulfillment === "delivery" ? <Truck size={12} /> : <PackageCheck size={12} />}
                {t(o.fulfillment)}
                {o.fulfillment === "delivery" && o.zone === "outside"
                  ? ` · ${o.city || t("zone_outside")}`
                  : ""}
              </Badge>
            </div>

            <div className="text-sm">
              <p className="font-medium">{o.customer.name}</p>
              {o.customer.phone && (
                <p className="text-muted-foreground flex items-center gap-1">
                  <Phone size={12} /> {o.customer.phone}
                </p>
              )}
              {o.customer.whatsapp && o.customer.whatsapp !== o.customer.phone && (
                <p className="text-muted-foreground flex items-center gap-1">
                  <MessageCircle size={12} /> {o.customer.whatsapp}
                </p>
              )}
              {o.customer.address && (
                <p className="text-muted-foreground flex items-start gap-1">
                  <MapPin size={12} className="mt-0.5" /> {o.customer.address}
                </p>
              )}
              {o.customer.lat && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${o.customer.lat},${o.customer.lng}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`map-link-${o.id}`}
                  className="text-primary text-xs underline"
                >
                  {t("open_map")}
                </a>
              )}
              {o.customer.note && (
                <p className="text-muted-foreground italic">“{o.customer.note}”</p>
              )}
              <div className="pt-2">
                <OrderContactButtons order={o} />
              </div>
            </div>

            <div className="border-t border-dashed pt-2 space-y-1 text-sm">
              {o.items.map((it, i) => (
                <div key={it.product_id || `${it.name}-${i}`} className="flex justify-between">
                  <span>
                    {it.name} <span className="text-muted-foreground">x{it.qty}</span>
                  </span>
                  <span className="num">{yen(it.price * it.qty)}</span>
                </div>
              ))}
              {o.loyalty_discount > 0 && (
                <div className="flex justify-between text-primary">
                  <span>
                    {t("loyalty_discount")} ({o.loyalty_pct}%)
                  </span>
                  <span className="num">-{yen(o.loyalty_discount)}</span>
                </div>
              )}
              {o.promo_discount > 0 && (
                <div className="flex justify-between text-primary">
                  <span>
                    {t("promo_discount")} ({o.promo_code})
                  </span>
                  <span className="num">-{yen(o.promo_discount)}</span>
                </div>
              )}
              {o.delivery_fee > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>{t("delivery_fee_short")}</span>
                  <span className="num">{yen(o.delivery_fee)}</span>
                </div>
              )}
              <div className="flex justify-between font-display font-semibold pt-1">
                <span>{t("total")}</span>
                <span className="num" data-testid={`online-order-total-${o.id}`}>
                  {yen(o.total)}
                </span>
              </div>            </div>

            <div className="flex gap-2 flex-wrap">
              {o.status === "new" && (
                <Button
                  size="sm"
                  data-testid={`process-order-${o.id}`}
                  onClick={() => setStatus(o, "processing")}
                >
                  {t("mark_processing")}
                </Button>
              )}
              {(o.status === "new" || o.status === "processing") && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`complete-order-${o.id}`}
                    onClick={() => setStatus(o, "done")}
                  >
                    {t("mark_done")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive"
                    data-testid={`cancel-order-${o.id}`}
                    onClick={() => setStatus(o, "cancelled")}
                  >
                    {t("cancel")}
                  </Button>
                </>
              )}
              {(o.status === "done" || o.status === "cancelled") && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive"
                  data-testid={`delete-online-order-${o.id}`}
                  onClick={async () => {
                    await api.delete(`/online-orders/${o.id}`);
                    load();
                  }}
                >
                  <Trash2 size={14} />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
